import { useCallback, useState } from 'react';

import { actionHelpers } from 'app-components/flux/core';
import {
  receiptsActions,
  trackReceiptEvent,
  type Receipt,
  type ReceiptDetachLocation,
} from 'app-components/flux/receipts';
import { transactionsActions } from 'app-components/flux/transactions';
import { getLogger } from 'app-components/utils/core';

import showConfirmationDialog from 'components/Dialogs/ConfirmationDialog/show';
import { useAppDispatch } from 'hooks/redux';

type DetachReceipt = {
  confirmDetach: () => void;
  isDetaching: boolean;
};

export const DETACH_RECEIPT_CONFIRMATION_CONTENT =
  'This transaction will be detached from the receipt, and the receipt will move back to Needs review.';

const log = getLogger('useDetachReceipt');

const useDetachReceipt = (
  receipt: Receipt | undefined,
  onDetached: () => void,
  location: ReceiptDetachLocation,
): DetachReceipt => {
  const appDispatch = useAppDispatch();
  const [isDetaching, setIsDetaching] = useState(false);
  const receiptId = receipt?.id;
  const transactionId = receipt?.transactionId;

  const detachReceipt = useCallback(async () => {
    if (!receiptId || !transactionId) {
      return;
    }
    setIsDetaching(true);
    try {
      await appDispatch(receiptsActions.unlinkReceiptFromTransaction(receiptId)).unwrap();
      try {
        await actionHelpers.bindPromiseAction(appDispatch, transactionsActions.getTransaction)({ id: transactionId });
      } catch (error) {
        log.error('Failed to refresh transaction after detaching receipt:', error);
      }
      trackReceiptEvent({ event: 'receiptDetach', properties: { location } });
      onDetached();
    } catch {
      return;
    } finally {
      setIsDetaching(false);
    }
  }, [appDispatch, location, onDetached, receiptId, transactionId]);

  const confirmDetach = useCallback(() => {
    showConfirmationDialog({
      id: 'unlink-receipt',
      title: 'Detach expense receipt?',
      content: DETACH_RECEIPT_CONFIRMATION_CONTENT,
      confirmLabel: 'Detach',
      confirmButtonVariant: 'delete',
      confirmAction: () => void detachReceipt(),
    });
  }, [detachReceipt]);

  return { confirmDetach, isDetaching };
};

export default useDetachReceipt;
